import { constant, noop } from 'lodash';
import { createContext, memo, type PropsWithChildren, useCallback, useContext, useMemo, useRef } from 'react';

import { type Board, type Clip } from '@air/api/types';
import { Spinner } from '@air/primitive-spinner';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import {
  DesktopAppWebSocket,
  type DesktopAppWebSocketSubscription,
} from '~/classes/desktopAppWebsocket/DesktopAppWebSocket';
import { Routes } from '~/constants/routes';
import { QueryParamNames } from '~/constants/search';
import { NEW_TAB } from '~/constants/urls';
import { isDevOrTestStage } from '~/swr-hooks/utils';

const LOADING_TOAST_DELAY_MS = 300;

type PendingOpenAction =
  | { kind: 'clip'; clip: Pick<Clip, 'id' | 'assetId'>; boardId?: Board['id'] }
  | { kind: 'board'; boardId: Board['id'] };

export type DesktopAppConnectionProviderValue = {
  isConnected: () => boolean;
  openClipInFinder: (clip: Pick<Clip, 'id' | 'assetId'>, boardId?: Board['id']) => void;
  openBoardInFinder: (boardId: Board['id']) => void;
};

const defaultValue: DesktopAppConnectionProviderValue = {
  isConnected: constant(false),
  openClipInFinder: noop,
  openBoardInFinder: noop,
};

const DesktopAppConnectionContext = createContext<DesktopAppConnectionProviderValue>(defaultValue);

export const DesktopAppConnectionProvider = memo(({ children }: PropsWithChildren<object>) => {
  const isAppConnected = useRef(false);
  const connectInFlightRef = useRef<Promise<boolean> | null>(null);
  const pendingOpenRef = useRef<PendingOpenAction | null>(null);
  const { showToast, dismissToast } = useToasts();

  const isConnected = useCallback(() => isAppConnected.current, []);

  const socketConnectionSubscription = useMemo<DesktopAppWebSocketSubscription>(
    () => ({
      onConnected: () => {
        isAppConnected.current = true;
      },
      onDisconnected: () => {
        isAppConnected.current = false;
      },
      onMessage: noop,
    }),
    [],
  );

  const connectWithLoadingState = useCallback(async () => {
    if (isAppConnected.current) {
      return true;
    }

    let loadingToastId: string | null = null;
    const loadingToastTimer = window.setTimeout(() => {
      loadingToastId = showToast('Launching Air Flow…', {
        prefix: <Spinner className="size-4" />,
        timeBeforeAutoDismiss: null,
      });
    }, LOADING_TOAST_DELAY_MS);

    const connected = await DesktopAppWebSocket.connect(socketConnectionSubscription);

    window.clearTimeout(loadingToastTimer);

    if (loadingToastId) {
      dismissToast(loadingToastId);
    }

    return connected;
  }, [dismissToast, showToast, socketConnectionSubscription]);

  const executePendingOpen = useCallback((action: PendingOpenAction, connected: boolean) => {
    if (connected) {
      if (action.kind === 'clip') {
        DesktopAppWebSocket.sendMessage({
          type: 'open-asset',
          data: {
            assetId: action.clip.id,
            boardId: action.boardId,
            asset: { id: action.clip.assetId },
          },
        });
        return;
      }

      DesktopAppWebSocket.sendMessage({
        type: 'open-board',
        data: {
          boardId: action.boardId,
        },
      });
      return;
    }

    if (action.kind === 'clip') {
      const searchParams = new URLSearchParams();
      searchParams.set(QueryParamNames.assetId, action.clip.assetId);
      searchParams.set(QueryParamNames.clipId, action.clip.id);
      if (action.boardId) {
        searchParams.set(QueryParamNames.boardId, action.boardId);
      }
      const url = `${window.location.origin}/${Routes.desktop.openDesktopFile}?${searchParams.toString()}`;
      window.open(url, NEW_TAB);
      return;
    }

    const searchParams = new URLSearchParams();
    searchParams.set(QueryParamNames.boardId, action.boardId);
    const url = `${window.location.origin}/${Routes.desktop.openDesktopFile}?${searchParams.toString()}`;
    window.open(url, NEW_TAB);
  }, []);

  const openAfterConnect = useCallback(
    (action: PendingOpenAction) => {
      if (isAppConnected.current) {
        executePendingOpen(action, true);
        return;
      }

      pendingOpenRef.current = action;

      if (connectInFlightRef.current) {
        return;
      }

      connectInFlightRef.current = connectWithLoadingState()
        .then((connected) => {
          const pending = pendingOpenRef.current;
          pendingOpenRef.current = null;

          if (pending) {
            executePendingOpen(pending, connected);
          }

          return connected;
        })
        .finally(() => {
          connectInFlightRef.current = null;
        });
    },
    [connectWithLoadingState, executePendingOpen],
  );

  const openClipInFinder = useCallback<DesktopAppConnectionProviderValue['openClipInFinder']>(
    (clip, boardId) => {
      openAfterConnect({ kind: 'clip', clip, boardId });
    },
    [openAfterConnect],
  );

  const openBoardInFinder = useCallback<DesktopAppConnectionProviderValue['openBoardInFinder']>(
    (boardId) => {
      openAfterConnect({ kind: 'board', boardId });
    },
    [openAfterConnect],
  );

  const value = useMemo<DesktopAppConnectionProviderValue>(
    () => ({
      isConnected,
      openClipInFinder,
      openBoardInFinder,
    }),
    [isConnected, openBoardInFinder, openClipInFinder],
  );

  return <DesktopAppConnectionContext.Provider value={value}>{children}</DesktopAppConnectionContext.Provider>;
});

DesktopAppConnectionProvider.displayName = 'DesktopAppConnectionProvider';

export const useDesktopAppConnectionContext = () => {
  const context = useContext(DesktopAppConnectionContext);

  if (context === defaultValue) {
    const error = 'useDesktopAppConnectionContext used outside of DesktopAppConnectionProvider';

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: 'Invalid usage of DesktopAppConnectionContext',
      });
    }
  }

  return context;
};
